import {useRef, useEffect, MouseEvent, FC} from 'react';
import {hashSum, useMemo, createGlobalState, createStore} from 'helpers';

interface PopupProps {
  deps?: any[];
  value?: any;
}

export const popups = createStore({});

export function usePopups() {
  return popups.useState()[0];
}

export function usePopup<T>(Component?: FC<T>, props?: T & PopupProps, type?: string) {
  const id = 'p' + hashSum(useMemo(Math.random, []));
  useEffect(() => {
    if (Component && props?.deps) {
      window.dispatchEvent(new CustomEvent(`popupsync-${id}`, {detail: props ?? {}}));
    }
  }, [Component, ...(props?.deps ?? [])]);
  if (Component) {
    return {
      id,
      open: () => window.dispatchEvent(new CustomEvent('popupopen', {detail: {id, Component, props: props ?? {}, type}})),
      close: () => window.dispatchEvent(new CustomEvent('popupclose', {detail: {id}})),
      toggle: () => window.dispatchEvent(new CustomEvent('popuptoggle', {detail: {id, Component, props: props ?? {}, type}}))
    };
  }
}

export function useFocusPopup<T>(Component: FC<T>, props?: T & PopupProps) {
  const popup = usePopup(Component, props, 'focus');
  if (popup) {
    return {
      'id': popup.id,
      'aria-haspopup': true,
      'tabIndex': 0,
      'onFocus': popup.open,
      'onClick': popup.open
    };
  }
}

export function useSuggestionPopup<T>(Component: FC, {value, deps = [value], ...props}: T & PopupProps) {
  const ref = useRef();
  const popup = usePopup(Component, {
    ...props,
    input: ref,
    value,
    deps
  }, 'suggestion');
  if (popup) {
    return [
      {
        'aria-haspopup': true,
        'id': popup.id
      },
      {
        'ref': ref,
        'onFocus': value ? undefined : popup.open,
        'onChange': (event: React.ChangeEvent<HTMLInputElement>) => {
          event.target.value ? popup.close() : popup.open();
        }
      }
    ];
  }
  return [];
}

export const openPopup = <T, >(Component: FC<T>, props?: T) => (event: MouseEvent) => {
  // in case it's inside <Link> or <NavLink>
  event.stopPropagation();
  event.preventDefault();
  const id = event.currentTarget.id || 'p' + hashSum(Math.random());
  event.currentTarget.id = id;
  window.dispatchEvent(new CustomEvent('popuptoggle', {
    detail: {
      id,
      Component,
      props: props ?? {},
      event
    }
  }));
};
